import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import toast from '../../design-system/toast';

import { Spinner } from '../../design-system';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useOrgApiKeys } from '../../hooks/useOrgApiKeys';
import { ApiKey } from '../../types/apiKey';
import { summarizeApiKeyScopes } from '../../utils/apiKeyScopes';
import CreateApiKeyModal from './CreateApiKeyModal';
import RevokeApiKeyModal from './RevokeApiKeyModal';

interface OrgApiKeysSectionProps {
  organizationId: string;
}

const formatRelativeOrNever = (iso: string | null): string => {
  if (!iso) return 'Never used';
  try {
    return `Last used ${formatDistanceToNow(new Date(iso), { addSuffix: true })}`;
  } catch {
    return 'Last used recently';
  }
};

const formatCreatedBy = (apiKey: ApiKey): string => {
  const created = new Date(apiKey.created_at).toLocaleDateString();
  const author = apiKey.created_by_name || apiKey.created_by_email || apiKey.created_by;
  return `Created on ${created} by ${author}`;
};

const OrgApiKeysSection: React.FC<OrgApiKeysSectionProps> = ({ organizationId }) => {
  const { isAdmin } = useOrganization();
  const { keys, isLoading, error, reload, createKey, revokeKey } = useOrgApiKeys(organizationId);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);

  const handleConfirmRevoke = async () => {
    if (!keyToRevoke) return;
    try {
      setRevokingKeyId(keyToRevoke.id);
      await revokeKey(keyToRevoke.id);
      toast.success('API key revoked');
      setKeyToRevoke(null);
    } catch (err: any) {
      const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to revoke API key';
      toast.error(typeof message === 'string' ? message : 'Failed to revoke API key');
    } finally {
      setRevokingKeyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={() => void reload()}
          className="mt-2 text-sm text-brand-primary dark:text-brand-primaryDarkMode hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-ui-text-tertiary" />
          <h3 className="text-lg font-semibold text-ui-text-primary">
            API Keys ({keys.length})
          </h3>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={!isAdmin}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-black
                     bg-brand-primary hover:bg-brand-primaryDark rounded-lg transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-primary"
        >
          <Plus className="w-4 h-4" />
          New API Key
        </button>
      </div>

      <p className="text-sm text-ui-text-secondary">
        API keys authenticate programmatic access to this organization via the public REST API at{' '}
        <span className="font-mono">/api/v1</span>. Scope each key to the least access needed for
        the integration.
      </p>

      {/* Empty state */}
      {keys.length === 0 ? (
        <div className="border border-dashed border-ui-border-primary rounded-lg p-8 text-center">
          <KeyRound className="w-10 h-10 mx-auto text-ui-text-tertiary mb-3" />
          <p className="text-sm font-medium text-ui-text-primary">No API keys yet</p>
          <p className="mt-1 text-sm text-ui-text-tertiary">
            Create an API key to authenticate programmatic access to this organization.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={!isAdmin}
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-black
                       bg-brand-primary hover:bg-brand-primaryDark rounded-lg transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-primary"
          >
            <Plus className="w-4 h-4" />
            New API Key
          </button>
        </div>
      ) : (
        <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary overflow-hidden">
          {keys.map((apiKey) => {
            const isRevokingThisKey = revokingKeyId === apiKey.id;
            const scopeSummary = summarizeApiKeyScopes(apiKey.scopes);
            return (
              <div
                key={apiKey.id}
                className="flex items-center justify-between gap-3 p-4 border-b border-ui-border-primary last:border-b-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-brand-primary/10 dark:bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                    <KeyRound className="w-4 h-4 text-brand-primary dark:text-brand-primaryDarkMode" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium text-ui-text-primary truncate">
                        {apiKey.name}
                      </p>
                      <span className="text-xs font-mono text-ui-text-tertiary truncate">
                        {apiKey.key_prefix}…
                      </span>
                      <span
                        className="inline-flex flex-shrink-0 items-center rounded-full border border-ui-border-primary px-2 py-0.5 text-xs font-medium text-ui-text-secondary"
                        title={scopeSummary.title}
                      >
                        {scopeSummary.label}
                      </span>
                    </div>
                    <p
                      className="text-xs text-ui-text-tertiary truncate"
                      title={apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString() : undefined}
                    >
                      {formatRelativeOrNever(apiKey.last_used_at)} · {formatCreatedBy(apiKey)}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setKeyToRevoke(apiKey)}
                  disabled={isRevokingThisKey || !isAdmin}
                  className="p-2 rounded text-ui-text-tertiary hover:text-red-600 dark:hover:text-red-400
                             hover:bg-ui-background-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  title="Revoke API key"
                  aria-label={`Revoke ${apiKey.name}`}
                >
                  {isRevokingThisKey ? (
                    <Spinner size={16} tone="current" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <CreateApiKeyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        createKey={createKey}
        onCreated={() => void reload()}
      />

      <RevokeApiKeyModal
        isOpen={keyToRevoke !== null}
        apiKey={keyToRevoke}
        isRevoking={revokingKeyId !== null}
        onClose={() => setKeyToRevoke(null)}
        onConfirm={handleConfirmRevoke}
      />
    </div>
  );
};

export default OrgApiKeysSection;
